import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { EntityLink } from "@/components/shared/relationships/EntityLink";
import {
  createPeekContent,
  LinkedRow,
  PeekHeader,
} from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useVendorCredit } from "@/features/vendor-credits/hooks/use-vendor-credit-data";
import type { VendorCreditApplication } from "@/features/vendor-credits/types";
import { ABSENT_VALUE, formatCurrency, formatEncodedDay, formatDocumentUid } from "@/lib/format";

function ApplicationHistoryRow({ application }: { application: VendorCreditApplication }) {
  const label = formatDocumentUid(
    application.gl_purchase_orders?.uid,
    application.purchase_order_id,
  );
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2">
        <EntityLink
          entity={{
            id: application.purchase_order_id,
            label,
            type: "purchase-order",
          }}
        >
          {label || "View"}
        </EntityLink>
        {application.is_reversal ? <Badge variant="destructive">Reversal</Badge> : null}
      </span>
      <MonoAmount>{formatCurrency(application.amount_applied)}</MonoAmount>
    </div>
  );
}

function VendorCreditContent({
  credit,
}: {
  credit: NonNullable<ReturnType<typeof useVendorCredit>["data"]>;
}) {
  const applications = credit.applications ?? [];

  return (
    <>
      <PeekHeader
        badge={credit.method ? <Badge variant="outline">{credit.method}</Badge> : null}
        title={credit.id.slice(0, 8)}
      />
      <p className="font-semibold text-2xl">
        <MonoAmount>{formatCurrency(credit.amount)}</MonoAmount>
      </p>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Available:</span>
        <Badge variant={(credit.available_balance ?? 0) > 0 ? "default" : "secondary"}>
          <MonoAmount>{formatCurrency(credit.available_balance ?? 0)}</MonoAmount>
        </Badge>
      </div>
      <Separator />
      <div className="space-y-1">
        <DetailRow label="Date" value={formatEncodedDay(credit.date)} />
        <DetailRow label="Reference" value={credit.reference || ABSENT_VALUE} />
        <LinkedRow
          id={credit.gl_accounts?.id}
          label="Vendor"
          name={credit.gl_accounts?.name}
          type="account"
        />
        {credit.gl_purchase_orders ? (
          <LinkedRow
            id={credit.gl_purchase_orders.id}
            label="Purchase Order"
            name={credit.gl_purchase_orders.uid}
            type="purchase-order"
          />
        ) : null}
      </div>
      <Separator />
      <div>
        <h3 className="mb-3 font-medium text-sm">Application History ({applications.length})</h3>
        {applications.length > 0 ? (
          <div className="space-y-2">
            {applications.slice(0, 10).map((application) => (
              <ApplicationHistoryRow application={application} key={application.id} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No applications yet</p>
        )}
      </div>
    </>
  );
}

/** Vendor-credit quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const VendorCreditPeekContent = createPeekContent({
  emptyMessage: "No credit found",
  render: (credit) => <VendorCreditContent credit={credit} />,
  useDetail: useVendorCredit,
});

registerPeekType("vendor-credit", VendorCreditPeekContent);
